/**
 * main.ts
 *
 * vite-ssg entry. Exports the ViteSSG app factory — we must NOT call
 * app.mount() here: vite-ssg mounts on the client and renderToString()s
 * on the server. The router is created and owned by vite-ssg from the
 * routes passed below (memory history on the server, web history on the
 * client).
 */

import { ViteSSG } from "vite-ssg";
import { setupLayouts } from "virtual:generated-layouts";
import { routes } from "vue-router/auto-routes";

import App from "./App.vue";
import { registerPlugins } from "@/plugins";

import "primeicons/primeicons.css";
import "./index.css";

export const createApp = ViteSSG(
  App,
  { routes: setupLayouts(routes), base: import.meta.env.BASE_URL },
  async (ctx) => {
    registerPlugins(ctx);

    // Everything below is client-only. Vueform touches `document` at import
    // time (so it must never load during the Node prerender) and is heavy
    // (~700KB) but only used on /auth form pages. Load + register it lazily
    // — only before a route that needs it resolves — so the indexed content
    // pages (home, /preview/:id, etc.) never download it. (For an initial
    // form-page load this still runs before mount, since vite-ssg awaits
    // router.isReady() before mounting.)
    if (ctx.isClient) {
      const { app, router } = ctx;

      let vueformReady: Promise<void> | undefined;
      const ensureVueform = () => {
        if (!vueformReady) {
          vueformReady = Promise.all([
            import("@vueform/vueform"),
            import("../vueform.config"),
          ]).then(([{ default: Vueform }, { default: vueformConfig }]) => {
            app.use(Vueform, vueformConfig);
          });
        }
        return vueformReady;
      };
      router.beforeResolve(async (to) => {
        if (/^\/auth(\/|$)/.test(to.path)) await ensureVueform();
      });

      // Browser-only router dynamic-import workaround (vitejs/vite#11804).
      router.onError((err, to) => {
        if (
          err?.message?.includes?.("Failed to fetch dynamically imported module")
        ) {
          if (!localStorage.getItem("vuetify:dynamic-reload")) {
            localStorage.setItem("vuetify:dynamic-reload", "true");
            location.assign(to.fullPath);
          } else {
            console.error(
              "Dynamic import error, reloading page did not fix it",
              err
            );
          }
        } else {
          console.error(err);
        }
      });
      router.isReady().then(() => {
        localStorage.removeItem("vuetify:dynamic-reload");
      });
    }
  }
);
